import { Preview } from "@creatomate/preview";

import { ElementSource, Subtitle, SubtitleSection } from "chq";

import { isStoryElement, timeToNumber } from "components/stories/video-studio/utils";
import { VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import hasBurnSubtitlesOn from "./hasBurnSubtitlesOn";
import createSubtitleElement from "./createSubtitleElement";
import addSpacesToText from "./addSpacesToText";
import parseTimes from "./parseTimes";

interface AdjustSubtitleTimeResult {
  keepSubtitle: boolean;
  elementStartTime: number;
  elementEndTime: number;
  referenceId?: string;
}

/*
adjust subtitles according to trimmins and position of the media, this only applies for new
generated subtitles because the ones that were previosuly created should be adjusted
by this moment (Edit an existing video)
*/
const adjustSubtitleTime = (
  startTime: number,
  endTime: number,
  originalsMedia?: ElementSource[]
): AdjustSubtitleTimeResult => {
  let keepSubtitle = true;
  const elementStartTime = startTime;
  const elementEndTime = endTime;

  if (!originalsMedia) return { keepSubtitle, elementStartTime, elementEndTime };

  const validRanges = originalsMedia.map(media => ({
    localTime: media.time,
    duration: media.duration,
    startTime: media.time,
    endTime: media.time + media.duration,
    referenceId: media.id
  }));

  const validRange = validRanges.find(
    range => (range.startTime < startTime && range.endTime > startTime)
  );

  if (!validRange) return { keepSubtitle: false, elementStartTime, elementEndTime };

  if ((validRange.startTime > endTime) || (validRange.endTime < startTime)) {
    keepSubtitle = false;
  }

  return { keepSubtitle, elementStartTime, elementEndTime, referenceId: validRange.referenceId };
};

const parseSubtitleSections = (
  subtitleText: string,
  originalsMedia?: ElementSource[]
): SubtitleSection[] => {
  const sections: SubtitleSection[] = [];
  const vttSections = subtitleText.split("\n\n");

  vttSections.forEach(section => {
    const parsedLine = section.trim().split("\n");

    if (parsedLine.length > 2) {
      const [index, time, text] = parsedLine;
      const { startTime, endTime } = parseTimes(time);
      const { keepSubtitle, elementStartTime, elementEndTime, referenceId } = adjustSubtitleTime(
        startTime,
        endTime,
        originalsMedia
      );

      if (keepSubtitle) {
        sections.push({
          text,
          startTime: elementStartTime,
          endTime: elementEndTime,
          index: parseInt(index, 10),
          originalStartTime: elementStartTime,
          originalText: text,
          originalEndTime: elementEndTime,
          referenceId: referenceId as string
        });
      }
    }
  });

  return sections;
};

const readVttSections = async (
  subtitle: Subtitle,
  active: boolean,
  preview: Preview,
  organizationName: string,
  language: string,
  isNewSubtitle = false
): Promise<void> => {
  let isMontage = false;
  const source = preview.getSource();
  const originalsMedia = source.elements.filter((element: ElementSource) => (
    element.type === VIDEO_TYPE && isStoryElement(element)
  ));
  const videoIsLandscape = source.width === 1280;
  const newElements: ElementSource[] = [];
  const newTrack = Math.max(...source.elements.map((element: ElementSource) => element.track)) + 1;

  const isOrientalFont = language?.includes("zh") || language === "jp" || language === "ja" || language === "ko";
  const landscapeInterval = isOrientalFont ? 28 : 50;
  const portraitInterval = isOrientalFont ? 15 : 28;
  const breakInterval = videoIsLandscape ? landscapeInterval : portraitInterval;

  // it means we are in a montage because we do not have media- elements
  if (originalsMedia.length === 0) {
    isMontage = true;
    originalsMedia.push(...source.elements.filter((element: ElementSource) => element.name?.includes("'s video")));
  }

  const sections = parseSubtitleSections(
    subtitle.text || "",
    originalsMedia
  );

  sections.forEach(section => {
    const subtitleElement = createSubtitleElement(
      addSpacesToText(section.text, breakInterval),
      section.index,
      section.startTime,
      section.endTime,
      newTrack,
      organizationName,
      section.referenceId,
      language,
      undefined,
      hasBurnSubtitlesOn(organizationName) && isNewSubtitle,
      active
    );
    newElements.push(subtitleElement);
  });

  await preview.setSource({ ...source, elements: [...source.elements, ...newElements] }, false);
};

export default readVttSections;
